import React, { useRef, useState, useEffect, useMemo, useCallback, useContext, createContext } from 'react';
import { Canvas, useThree } from '@react-three/fiber';
import { OrbitControls, Html } from '@react-three/drei';
import * as THREE from 'three';
import { parseFeet, formatFeet, formatSize, parseTileSizeInches, clamp } from '../utils/roomUnits';

/**
 * RoomVisualizer3D — photoreal-ish preview of a Room Finish schedule.
 *
 * Generalized from ThreeShowerView: the room shell is driven by real dimensions
 * (feet), surfaces take paint colors as well as image textures, and placed
 * objects / material patches are draggable and resizable. The original shower
 * render is preserved as the `shower` preset.
 */

const API_URL = (window.ENV?.REACT_APP_BACKEND_URL || process.env.REACT_APP_BACKEND_URL || window.location.origin);
const proxyUrl = (url) => (url ? `${API_URL}/api/proxy-image?url=${encodeURIComponent(url)}` : null);

export const DEFAULT_ROOM_CONFIG = { width_ft: 14, depth_ft: 12, height_ft: 9, wall_count: 3, preset: 'room' };
export const SHOWER_ROOM_CONFIG = { width_ft: 5, depth_ft: 4, height_ft: 8, wall_count: 3, preset: 'shower' };

/** Object types the user can drop into the room. Sizes are sensible defaults in feet. */
export const OBJECT_PRESETS = [
  { key: 'sofa',    label: 'Sofa',    anchor: 'floor',   w: 7,   h: 2.8, cutout: true },
  { key: 'chair',   label: 'Chair',   anchor: 'floor',   w: 2.8, h: 3.0, cutout: true },
  { key: 'table',   label: 'Table',   anchor: 'floor',   w: 4,   h: 1.6, cutout: true },
  { key: 'console', label: 'Console', anchor: 'floor',   w: 4,   h: 2.7, cutout: true },
  { key: 'plant',   label: 'Plant',   anchor: 'floor',   w: 2,   h: 4.0, cutout: true },
  { key: 'rug',     label: 'Rug',     anchor: 'rug',     w: 8,   h: 10,  cutout: false },
  { key: 'art',     label: 'Art',     anchor: 'wall',    w: 3,   h: 3.8, y: 5.2, cutout: false, frame: true },
  { key: 'mirror',  label: 'Mirror',  anchor: 'wall',    w: 3,   h: 4.0, y: 5.2, cutout: false, frame: true },
  { key: 'sconce',  label: 'Sconce',  anchor: 'wall',    w: 0.9, h: 1.2, y: 5.8, cutout: true },
  { key: 'pendant', label: 'Pendant', anchor: 'ceiling', w: 1.8, h: 2.4, cutout: true },
];
const PRESET_BY_KEY = OBJECT_PRESETS.reduce((m, p) => { m[p.key] = p; return m; }, {});

export const PAINT_SWATCHES = [
  { hex: '#F4F1EA', name: 'Alabaster' },
  { hex: '#E3DED3', name: 'Natural Linen' },
  { hex: '#C9C6BC', name: 'Warm Gray' },
  { hex: '#8E9A93', name: 'Sage' },
  { hex: '#4F5D57', name: 'Forest' },
  { hex: '#2E3B45', name: 'Deep Slate' },
  { hex: '#1C1C1C', name: 'Tricorn Black' },
  { hex: '#7C4A3A', name: 'Terracotta' },
];

/* ============================ image + pattern caches ============================ */
/* The expensive work (network fetch, pattern canvas) is cached module-wide and
   shared across every surface; only the cheap THREE.Texture wrapper is per-mesh. */

const imageCache = new Map();   // url -> Promise<HTMLImageElement>
const patternCache = new Map(); // key -> { source, cols }
const cutoutCache = new Map();  // url -> HTMLCanvasElement

function loadImage(url) {
  if (imageCache.has(url)) return imageCache.get(url);
  const p = fetch(proxyUrl(url))
    .then((r) => { if (!r.ok) throw new Error('proxy fetch failed'); return r.blob(); })
    .then((blob) => new Promise((res) => {
      const fr = new FileReader();
      fr.onload = () => res(fr.result);
      fr.readAsDataURL(blob);
    }))
    .then((dataUrl) => new Promise((res, rej) => {
      const img = new Image();
      img.onload = () => res(img);
      img.onerror = () => rej(new Error('decode failed'));
      img.src = dataUrl;
    }));
  imageCache.set(url, p);
  p.catch(() => imageCache.delete(url)); // never cache a failure permanently
  return p;
}

/** Knock white catalog backgrounds out of furniture photos so they read as objects, not cards. */
function keyOutWhite(img, cacheKey) {
  if (cacheKey && cutoutCache.has(cacheKey)) return cutoutCache.get(cacheKey);
  const c = document.createElement('canvas');
  c.width = img.width; c.height = img.height;
  const ctx = c.getContext('2d', { willReadFrequently: true });
  ctx.drawImage(img, 0, 0);
  try {
    const data = ctx.getImageData(0, 0, c.width, c.height);
    const px = data.data;
    for (let i = 0; i < px.length; i += 4) {
      const r = px[i], g = px[i + 1], b = px[i + 2];
      const max = Math.max(r, g, b), min = Math.min(r, g, b);
      // Near-white and near-neutral goes fully transparent; the band just below
      // it feathers out so cut edges do not alias into a hard white halo.
      if (min > 244 && max - min < 12) px[i + 3] = 0;
      else if (min > 230 && max - min < 18) px[i + 3] = Math.round(((244 - min) / 14) * 255);
    }
    ctx.putImageData(data, 0, 0);
  } catch (e) { /* tainted canvas — fall back to the opaque image */ }
  if (cacheKey) cutoutCache.set(cacheKey, c);
  return c;
}

/** Ported from ThreeShowerView — builds the repeating pattern canvas from one tile. */
function buildPatternSource(img, pattern, tileCrop, mode) {
  const cropKey = tileCrop ? `${tileCrop.x}_${tileCrop.y}_${tileCrop.w}_${tileCrop.h}` : 'none';
  const key = `${String(img.src || '').slice(-64)}|${pattern}|${cropKey}|${mode}`;
  if (patternCache.has(key)) return patternCache.get(key);

  // STEP 1: crop to a single tile, but only for patterns that rearrange tiles.
  // stacked_horizontal uses the sheet as-is because the sheet already IS that pattern.
  let tileImg = img;
  const needsCrop = pattern && pattern !== 'stacked_horizontal';
  if (mode !== 'full' && needsCrop && tileCrop && tileCrop.w > 0 && tileCrop.h > 0) {
    const cropCanvas = document.createElement('canvas');
    cropCanvas.width = tileCrop.w;
    cropCanvas.height = tileCrop.h;
    cropCanvas.getContext('2d').drawImage(img, tileCrop.x, tileCrop.y, tileCrop.w, tileCrop.h, 0, 0, tileCrop.w, tileCrop.h);
    tileImg = cropCanvas;
  }

  const imgW = tileImg.width, imgH = tileImg.height;
  let sourceCanvas = null;
  let colsInCanvas = 1;

  if (mode !== 'full') {
    const sampleCanvas = document.createElement('canvas');
    sampleCanvas.width = 1; sampleCanvas.height = 1;
    const sc = sampleCanvas.getContext('2d', { willReadFrequently: true });
    sc.drawImage(tileImg, 0, 0, imgW, imgH, 0, 0, 1, 1);
    let bgColor = '#808080';
    try {
      const px = sc.getImageData(0, 0, 1, 1).data;
      bgColor = `rgb(${px[0]},${px[1]},${px[2]})`;
    } catch (e) { /* tainted */ }

    switch (pattern) {
      case 'offset': {
        sourceCanvas = document.createElement('canvas');
        sourceCanvas.width = imgW * 2; sourceCanvas.height = imgH * 2;
        const p = sourceCanvas.getContext('2d');
        p.fillStyle = bgColor; p.fillRect(0, 0, sourceCanvas.width, sourceCanvas.height);
        p.drawImage(tileImg, 0, 0); p.drawImage(tileImg, imgW, 0);
        p.drawImage(tileImg, -imgW / 2, imgH); p.drawImage(tileImg, imgW / 2, imgH); p.drawImage(tileImg, imgW * 1.5, imgH);
        colsInCanvas = 2;
        break;
      }
      case 'one_third_offset': {
        sourceCanvas = document.createElement('canvas');
        sourceCanvas.width = imgW * 3; sourceCanvas.height = imgH * 3;
        const p = sourceCanvas.getContext('2d');
        p.fillStyle = bgColor; p.fillRect(0, 0, sourceCanvas.width, sourceCanvas.height);
        for (let c = 0; c < 4; c++) p.drawImage(tileImg, c * imgW, 0);
        for (let c = -1; c < 4; c++) p.drawImage(tileImg, c * imgW + imgW / 3, imgH);
        for (let c = -1; c < 4; c++) p.drawImage(tileImg, c * imgW + (imgW * 2 / 3), imgH * 2);
        colsInCanvas = 3;
        break;
      }
      case 'stacked_vertical': {
        sourceCanvas = document.createElement('canvas');
        sourceCanvas.width = imgH; sourceCanvas.height = imgW;
        const p = sourceCanvas.getContext('2d');
        p.fillStyle = bgColor; p.fillRect(0, 0, sourceCanvas.width, sourceCanvas.height);
        p.translate(imgH / 2, imgW / 2); p.rotate(Math.PI / 2);
        p.drawImage(tileImg, -imgW / 2, -imgH / 2);
        colsInCanvas = 1;
        break;
      }
      case 'herringbone': {
        const tileLen = Math.max(imgW, imgH);
        const tileThk = Math.min(imgW, imgH);
        const size = (tileLen + tileThk) * 3;
        sourceCanvas = document.createElement('canvas');
        sourceCanvas.width = size; sourceCanvas.height = size;
        const p = sourceCanvas.getContext('2d');
        p.fillStyle = bgColor; p.fillRect(0, 0, size, size);
        for (let row = -4; row < size / tileThk + 4; row++) {
          for (let col = -4; col < size / tileLen + 4; col++) {
            const bx = col * tileLen;
            const by = row * tileThk * 2 + (col % 2) * tileThk;
            p.drawImage(tileImg, 0, 0, imgW, imgH, bx, by, tileLen, tileThk);
            p.save();
            p.translate(bx + tileLen / 2, by + tileThk + tileLen / 2);
            p.rotate(Math.PI / 2);
            p.drawImage(tileImg, 0, 0, imgW, imgH, -tileLen / 2, -tileThk / 2, tileLen, tileThk);
            p.restore();
          }
        }
        colsInCanvas = Math.max(1, Math.round(size / tileLen));
        break;
      }
      case 'basket_weave': {
        sourceCanvas = document.createElement('canvas');
        sourceCanvas.width = imgW * 4; sourceCanvas.height = imgH * 4;
        const p = sourceCanvas.getContext('2d');
        p.fillStyle = bgColor; p.fillRect(0, 0, sourceCanvas.width, sourceCanvas.height);
        for (let gy = 0; gy < 4; gy++) for (let gx = 0; gx < 4; gx++) {
          const bx = gx * imgW, by = gy * imgH;
          if ((gx + gy) % 2 === 0) {
            p.drawImage(tileImg, 0, 0, imgW, imgH, bx, by, imgW, imgH / 2);
            p.drawImage(tileImg, 0, 0, imgW, imgH, bx, by + imgH / 2, imgW, imgH / 2);
          } else {
            p.save(); p.translate(bx + imgW / 2, by + imgH / 2); p.rotate(Math.PI / 2);
            p.drawImage(tileImg, 0, 0, imgW, imgH, -imgH / 2, -imgW / 2, imgH, imgW);
            p.restore();
          }
        }
        colsInCanvas = 4;
        break;
      }
      default: break; // stacked_horizontal: raw tile repeats
    }
  }

  // Rows matter as much as columns: the vertical repeat has to be derived from
  // the tile's real height, and the pattern canvas may stack several rows.
  const rowsInCanvas = sourceCanvas && imgH > 0
    ? Math.max(1, Math.round(sourceCanvas.height / imgH))
    : 1;

  const result = { source: sourceCanvas || tileImg, cols: colsInCanvas, rows: rowsInCanvas };
  patternCache.set(key, result);
  return result;
}

/* ============================ texture hooks ============================ */

/**
 * Texture for a finish on a surface. When the product's `size` field parses
 * (e.g. "24x48"), the repeat is derived from real dimensions so a 2ft tile on a
 * 14ft wall reads as 7 tiles — not an arbitrary constant.
 */
/**
 * Assumed tile size when a product has no usable `size` field. A 12in square
 * reads as tile; the old heuristic (a few repeats across the whole wall) made
 * every unsized material look like enormous slabs.
 */
const DEFAULT_TILE_INCHES = 12;

/**
 * How many times a tile image repeats across a surface, in each axis.
 *
 * Both axes come from the tile's REAL size in inches, so a 4x12 tile covers
 * 4in across and 12in up regardless of the proportions of the product photo.
 * `cols`/`rows` say how many tiles the pattern canvas already contains, so a
 * basket-weave sheet of 4x4 tiles is not counted as one giant tile.
 */
export function tileRepeat({ surfaceW, surfaceH, tileSize, tileScale = 1, cols = 1, rows = 1 }) {
  const inches = parseTileSizeInches(tileSize);
  const scale = tileScale > 0 ? tileScale : 1;
  const tileWft = ((inches?.w ?? DEFAULT_TILE_INCHES) / 12) * scale;
  const tileHft = ((inches?.h ?? DEFAULT_TILE_INCHES) / 12) * scale;
  return {
    x: (surfaceW / Math.max(0.05, tileWft)) / Math.max(1, cols),
    y: (surfaceH / Math.max(0.05, tileHft)) / Math.max(1, rows),
    tileWft,
    tileHft,
  };
}

function useSurfaceTexture({
  imageUrl, mode = 'sheet', pattern = 'stacked_horizontal', tileCrop = null,
  surfaceW = 1, surfaceH = 1, tileSize = '', tileScale = 1,
}) {
  const { invalidate } = useThree();
  const [tex, setTex] = useState(null);
  const cropKey = tileCrop ? `${tileCrop.x}_${tileCrop.y}_${tileCrop.w}_${tileCrop.h}` : 'none';

  useEffect(() => {
    if (!imageUrl) { setTex(null); return undefined; }
    let cancelled = false;
    let created = null;

    loadImage(imageUrl).then((img) => {
      if (cancelled) return;
      const { source, cols, rows } = buildPatternSource(img, pattern, tileCrop, mode);
      const t = new THREE.Texture(source);
      t.colorSpace = THREE.SRGBColorSpace;
      t.anisotropy = 4;

      if (mode === 'full') {
        t.wrapS = THREE.ClampToEdgeWrapping;
        t.wrapT = THREE.ClampToEdgeWrapping;
        t.repeat.set(1, 1);
      } else {
        t.wrapS = THREE.RepeatWrapping;
        t.wrapT = THREE.RepeatWrapping;
        const { x: rx, y: ry } = tileRepeat({
          surfaceW, surfaceH, tileSize, tileScale, cols, rows,
        });
        t.repeat.set(rx, ry);
      }
      t.needsUpdate = true;
      created = t;
      setTex(t);
      invalidate();
    }).catch(() => { /* leave the surface on its fallback color */ });

    return () => { cancelled = true; if (created) created.dispose(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imageUrl, mode, pattern, cropKey, surfaceW, surfaceH, tileSize, tileScale, invalidate]);

  return tex;
}

/** Plain (non-tiling) texture for a placed object's product photo. */
function useObjectTexture(imageUrl, cutout) {
  const { invalidate } = useThree();
  const [tex, setTex] = useState(null);

  useEffect(() => {
    if (!imageUrl) { setTex(null); return undefined; }
    let cancelled = false;
    let created = null;

    loadImage(imageUrl).then((img) => {
      if (cancelled) return;
      const source = cutout ? keyOutWhite(img, imageUrl) : img;
      const t = new THREE.Texture(source);
      t.colorSpace = THREE.SRGBColorSpace;
      t.anisotropy = 4;
      t.needsUpdate = true;
      created = t;
      setTex(t);
      invalidate();
    }).catch(() => {});

    return () => { cancelled = true; if (created) created.dispose(); };
  }, [imageUrl, cutout, invalidate]);

  return tex;
}

/** Soft radial blob reused as the contact shadow under every floor object. */
let _shadowTex = null;
function getShadowTexture() {
  if (_shadowTex) return _shadowTex;
  const c = document.createElement('canvas');
  c.width = 128; c.height = 128;
  const ctx = c.getContext('2d');
  const g = ctx.createRadialGradient(64, 64, 0, 64, 64, 64);
  g.addColorStop(0, 'rgba(0,0,0,0.55)');
  g.addColorStop(0.6, 'rgba(0,0,0,0.18)');
  g.addColorStop(1, 'rgba(0,0,0,0)');
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, 128, 128);
  _shadowTex = new THREE.CanvasTexture(c);
  return _shadowTex;
}

/** Memoized outline geometry for the selection box, disposed when it changes. */
function useEdgeGeometry(w, h) {
  const geo = useMemo(() => {
    const plane = new THREE.PlaneGeometry(w, h);
    const edges = new THREE.EdgesGeometry(plane);
    plane.dispose();
    return edges;
  }, [w, h]);
  useEffect(() => () => geo.dispose(), [geo]);
  return geo;
}

/* ============================ geometry helpers ============================ */

const WALL_ROLES = ['back', 'left', 'right', 'front'];

/** Map surfaces onto room roles by name, falling back to declaration order. */
export function getSurfaceRoles(surfaces = []) {
  const roles = {};
  const walls = surfaces.filter((s) => s.surface_type === 'wall');
  const claimed = new Set();

  WALL_ROLES.forEach((role) => {
    const match = walls.find((s) => !claimed.has(s.id) && (s.name || '').toLowerCase().includes(role));
    if (match) { roles[role] = match; claimed.add(match.id); }
  });
  WALL_ROLES.forEach((role) => {
    if (roles[role]) return;
    const next = walls.find((s) => !claimed.has(s.id));
    if (next) { roles[role] = next; claimed.add(next.id); }
  });

  roles.floor = surfaces.find((s) => s.surface_type === 'floor') || null;
  roles.ceiling = surfaces.find((s) => s.surface_type === 'ceiling') || null;
  return roles;
}

/** World transform + face size for a wall role. */
export function wallTransform(role, { width_ft: W, depth_ft: D, height_ft: H }) {
  switch (role) {
    case 'back':  return { position: [0, H / 2, -D / 2], rotation: [0, 0, 0],            w: W, h: H, normal: [0, 0, 1],  point: [0, 0, -D / 2] };
    case 'front': return { position: [0, H / 2,  D / 2], rotation: [0, Math.PI, 0],      w: W, h: H, normal: [0, 0, -1], point: [0, 0,  D / 2] };
    case 'left':  return { position: [-W / 2, H / 2, 0], rotation: [0, Math.PI / 2, 0],  w: D, h: H, normal: [1, 0, 0],  point: [-W / 2, 0, 0] };
    case 'right': return { position: [ W / 2, H / 2, 0], rotation: [0, -Math.PI / 2, 0], w: D, h: H, normal: [-1, 0, 0], point: [ W / 2, 0, 0] };
    default: return null;
  }
}

const getMaterialFor = (surface, zoneId) => {
  if (!surface) return null;
  const mats = surface.materials || [];
  return mats.find((m) => m.position_label === zoneId && (m.image || m.swatch_color))
    || mats.find((m) => m.position_label === 'main_wall' && (m.image || m.swatch_color))
    || mats.find((m) => m.position_label === '_surface' && (m.image || m.swatch_color))
    || mats.find((m) => m.image || m.swatch_color)
    || null;
};

/** The world plane an object is constrained to slide along. */
function anchorPlane(anchor, role, roomCfg) {
  const { depth_ft: D, height_ft: H } = roomCfg;
  const plane = new THREE.Plane();
  if (anchor === 'wall') {
    const t = wallTransform(role, roomCfg);
    if (!t) return plane.setFromNormalAndCoplanarPoint(new THREE.Vector3(0, 0, 1), new THREE.Vector3(0, 0, -D / 2));
    return plane.setFromNormalAndCoplanarPoint(new THREE.Vector3(...t.normal), new THREE.Vector3(...t.point));
  }
  if (anchor === 'ceiling') {
    return plane.setFromNormalAndCoplanarPoint(new THREE.Vector3(0, -1, 0), new THREE.Vector3(0, H, 0));
  }
  return plane.setFromNormalAndCoplanarPoint(new THREE.Vector3(0, 1, 0), new THREE.Vector3(0, 0, 0));
}

function rayFromDomEvent(ev, camera, gl) {
  const rect = gl.domElement.getBoundingClientRect();
  const ndc = new THREE.Vector2(
    ((ev.clientX - rect.left) / rect.width) * 2 - 1,
    -((ev.clientY - rect.top) / rect.height) * 2 + 1,
  );
  const rc = new THREE.Raycaster();
  rc.setFromCamera(ndc, camera);
  return rc.ray;
}

/* ============================ shared viz context ============================ */

const VizContext = createContext({
  roomCfg: DEFAULT_ROOM_CONFIG,
  lighting: 'flat',
  selection: null,
  setSelection: () => {},
  setDragging: () => {},
  update: () => {},
  setLiveLabel: () => {},
});

/**
 * Starts a pointer-driven drag against a fixed world plane. Window-level
 * listeners keep the drag alive when the cursor leaves the mesh or the canvas.
 */
function useWorldDrag() {
  const { camera, gl, invalidate } = useThree();
  const { setDragging } = useContext(VizContext);

  return useCallback((plane, onMove, onEnd) => {
    const hit = new THREE.Vector3();
    const move = (ev) => {
      const ray = rayFromDomEvent(ev, camera, gl);
      if (ray.intersectPlane(plane, hit)) { onMove(hit.clone()); invalidate(); }
    };
    const up = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      setDragging(false);
      if (onEnd) onEnd();
      invalidate();
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    setDragging(true);
  }, [camera, gl, invalidate, setDragging]);
}

/**
 * Captures the object's world-space basis at drag start so resize math never
 * reads stale width/height out of a closure mid-drag.
 */
function captureBasis(group, sx, sy, w, h) {
  const quat = group.getWorldQuaternion(new THREE.Quaternion());
  const right = new THREE.Vector3(1, 0, 0).applyQuaternion(quat);
  const up = new THREE.Vector3(0, 1, 0).applyQuaternion(quat);
  const normal = new THREE.Vector3().crossVectors(right, up).normalize();
  const oppWorld = group.localToWorld(new THREE.Vector3(-sx * w / 2, -sy * h / 2, 0));
  const plane = new THREE.Plane().setFromNormalAndCoplanarPoint(
    normal, group.getWorldPosition(new THREE.Vector3()),
  );
  return { right, up, oppWorld, plane, parent: group.parent };
}

const MIN_ITEM_FT = 0.25;

/** How large an item may get, given the surface it lives on. */
function extentsFor(anchor, role, roomCfg) {
  const { width_ft: W, depth_ft: D, height_ft: H } = roomCfg;
  if (anchor === 'wall') {
    const t = wallTransform(role, roomCfg);
    return { maxW: t ? t.w : W, maxH: H };
  }
  if (anchor === 'rug') return { maxW: W, maxH: D };
  return { maxW: W, maxH: H };
}

/**
 * Given a drag hit, derive the new extents and centre from a fixed opposite
 * corner. `bounds` caps the size to the surface the item sits on, so dragging a
 * corner can no longer grow an item straight through the walls. The centre is
 * recomputed from the clamped extents, so the opposite corner still stays put.
 */
function resolveResize(hit, { right, up, oppWorld, parent }, bounds = {}) {
  const d = hit.clone().sub(oppWorld);
  const rawW = d.dot(right);
  const rawH = d.dot(up);
  const newW = clamp(Math.abs(rawW), MIN_ITEM_FT, bounds.maxW ?? Infinity);
  const newH = clamp(Math.abs(rawH), MIN_ITEM_FT, bounds.maxH ?? Infinity);
  const alongW = (rawW < 0 ? -1 : 1) * newW;
  const alongH = (rawH < 0 ? -1 : 1) * newH;
  const centerWorld = oppWorld.clone()
    .add(right.clone().multiplyScalar(alongW / 2))
    .add(up.clone().multiplyScalar(alongH / 2));
  return { newW, newH, local: parent.worldToLocal(centerWorld.clone()) };
}

/* ============================ surface meshes ============================ */

const SurfaceFace = ({ size, texture, color, lighting, ...props }) => {
  const lit = lighting === 'soft';
  return (
    <mesh {...props}>
      <planeGeometry args={size} />
      {texture ? (
        lit
          ? <meshStandardMaterial key={`s-${texture.id}`} map={texture} roughness={0.9} metalness={0.02} side={THREE.FrontSide} />
          : <meshBasicMaterial key={`b-${texture.id}`} map={texture} color="#ffffff" side={THREE.FrontSide} />
      ) : (
        lit
          ? <meshStandardMaterial key="s-plain" color={color} roughness={0.9} metalness={0.02} side={THREE.FrontSide} />
          : <meshBasicMaterial key="b-plain" color={color} side={THREE.FrontSide} />
      )}
    </mesh>
  );
};

/** One finish zone (main wall / wainscot / upper accent) on a wall face. */
const ZoneFace = ({ material, width, height, y, lighting }) => {
  const texture = useSurfaceTexture({
    imageUrl: material?.image || null,
    mode: material?.display_mode || 'sheet',
    pattern: material?.pattern || 'stacked_horizontal',
    tileCrop: material?.tile_crop || null,
    surfaceW: width,
    surfaceH: height,
    tileSize: material?.size || '',
    tileScale: material?.tile_scale || 1,
  });
  return (
    <SurfaceFace
      position={[0, y, 0]}
      size={[width, height]}
      texture={texture}
      color={material?.swatch_color || '#8d8d8d'}
      lighting={lighting}
    />
  );
};

/* ============================ placed objects ============================ */

const HANDLE_CORNERS = [[-1, -1], [1, -1], [-1, 1], [1, 1]];

const handleStyle = {
  width: 12, height: 12, borderRadius: 3,
  background: '#22c55e', border: '2px solid #0b1a10',
  cursor: 'nwse-resize', pointerEvents: 'auto',
  boxShadow: '0 1px 4px rgba(0,0,0,0.6)',
};

/**
 * A product placed in the room. Furniture stands on the floor, art and sconces
 * pin to a wall, rugs lie flat — each constrained to its own anchor plane.
 */
const PlacedObject = ({ obj, role }) => {
  const { roomCfg, lighting, selection, setSelection, update, setLiveLabel } = useContext(VizContext);
  const groupRef = useRef();
  const beginDrag = useWorldDrag();
  const { height_ft: H, width_ft: W, depth_ft: D } = roomCfg;

  const preset = PRESET_BY_KEY[obj.type] || {};
  const cutout = obj.cutout !== undefined ? obj.cutout : !!preset.cutout;
  const texture = useObjectTexture(obj.image || null, cutout);
  const selected = selection?.kind === 'object' && selection.id === obj.id;

  const w = Math.max(0.2, obj.w || preset.w || 3);
  const h = Math.max(0.2, obj.h || preset.h || 3);
  const rotY = obj.rot_y || 0;
  const edgeGeo = useEdgeGeometry(w, h);

  const transform = useMemo(() => {
    switch (obj.anchor) {
      case 'wall':    return { position: [obj.x || 0, (obj.y ?? 5) - H / 2, 0.04], rotation: [0, 0, 0] };
      case 'rug':     return { position: [obj.x || 0, 0.02, obj.z || 0], rotation: [-Math.PI / 2, 0, rotY] };
      case 'ceiling': return { position: [obj.x || 0, H - h / 2, obj.z || 0], rotation: [0, rotY, 0] };
      default:        return { position: [obj.x || 0, h / 2, obj.z || 0], rotation: [0, rotY, 0] };
    }
  }, [obj.anchor, obj.x, obj.y, obj.z, rotY, h, H]);

  const patch = useCallback((changes) => {
    update((prev) => ({
      ...prev,
      objects: (prev.objects || []).map((o) => (o.id === obj.id ? { ...o, ...changes } : o)),
    }));
  }, [obj.id, update]);

  const onBodyPointerDown = (e) => {
    e.stopPropagation();
    setSelection({ kind: 'object', id: obj.id });
    const group = groupRef.current;
    const parent = group?.parent;
    if (!parent) return;
    const plane = anchorPlane(obj.anchor, role, roomCfg);

    beginDrag(plane, (worldHit) => {
      const local = parent.worldToLocal(worldHit.clone());
      if (obj.anchor === 'wall') {
        const t = wallTransform(role, roomCfg);
        const halfW = (t?.w || W) / 2;
        const y = clamp(local.y + H / 2, h / 2, H - h / 2);
        patch({ x: clamp(local.x, -halfW + w / 2, halfW - w / 2), y });
        setLiveLabel(`${formatFeet(y)} A.F.F.`);
      } else {
        patch({
          x: clamp(local.x, -W / 2 + w / 2, W / 2 - w / 2),
          z: clamp(local.z, -D / 2 + 0.2, D / 2 - 0.2),
        });
        setLiveLabel(formatSize(w, h));
      }
    }, () => setLiveLabel(null));
  };

  const onHandlePointerDown = (ev, sx, sy) => {
    ev.stopPropagation();
    ev.preventDefault();
    const group = groupRef.current;
    if (!group) return;
    setSelection({ kind: 'object', id: obj.id });
    const basis = captureBasis(group, sx, sy, w, h);
    const bounds = extentsFor(obj.anchor, role, roomCfg);

    beginDrag(basis.plane, (worldHit) => {
      const { newW, newH, local } = resolveResize(worldHit, basis, bounds);
      // Re-clamp the centre too, so the whole item stays inside its surface.
      const changes = { w: newW, h: newH };
      if (obj.anchor === 'wall') {
        const halfW = bounds.maxW / 2;
        changes.x = clamp(local.x, -halfW + newW / 2, halfW - newW / 2);
        changes.y = clamp(local.y + H / 2, newH / 2, H - newH / 2);
      } else if (obj.anchor === 'rug') {
        changes.x = clamp(local.x, -W / 2 + newW / 2, W / 2 - newW / 2);
        changes.z = clamp(local.z, -D / 2 + newH / 2, D / 2 - newH / 2);
      } else {
        changes.x = clamp(local.x, -W / 2 + newW / 2, W / 2 - newW / 2);
        changes.z = clamp(local.z, -D / 2 + 0.2, D / 2 - 0.2);
      }
      patch(changes);
      setLiveLabel(formatSize(newW, newH));
    }, () => setLiveLabel(null));
  };

  const frame = obj.frame !== undefined ? obj.frame : !!preset.frame;

  return (
    <group ref={groupRef} {...transform} name={`obj:${obj.id}`}>
      {/* Frame behind wall art / mirrors */}
      {frame && (
        <mesh position={[0, 0, -0.012]}>
          <planeGeometry args={[w + 0.12, h + 0.12]} />
          <meshBasicMaterial color={obj.frame_color || '#141414'} side={THREE.FrontSide} />
        </mesh>
      )}

      <mesh onPointerDown={onBodyPointerDown}>
        <planeGeometry args={[w, h]} />
        {texture ? (
          lighting === 'soft'
            ? <meshStandardMaterial key={`os-${texture.id}`} map={texture} transparent alphaTest={0.05} roughness={0.85} side={THREE.DoubleSide} />
            : <meshBasicMaterial key={`ob-${texture.id}`} map={texture} transparent alphaTest={0.05} side={THREE.DoubleSide} />
        ) : (
          <meshBasicMaterial key="ob-plain" color={obj.placeholder_color || '#7c8b99'} transparent opacity={0.55} side={THREE.DoubleSide} />
        )}
      </mesh>

      {/* Floor objects get a contact shadow and a faint reflection, which is what
          sells them as standing in the room rather than pasted onto it. */}
      {obj.anchor === 'floor' && (
        <>
          <mesh position={[0, -h / 2 + 0.01, 0.05]} rotation={[-Math.PI / 2, 0, 0]}>
            <planeGeometry args={[w * 1.15, Math.max(1, w * 0.5)]} />
            <meshBasicMaterial map={getShadowTexture()} transparent depthWrite={false} opacity={0.85} />
          </mesh>
          {texture && (
            <mesh position={[0, -h, 0]} scale={[1, -1, 1]}>
              <planeGeometry args={[w, h]} />
              <meshBasicMaterial map={texture} transparent opacity={0.1} alphaTest={0.05} depthWrite={false} side={THREE.DoubleSide} />
            </mesh>
          )}
        </>
      )}

      {selected && (
        <>
          <lineSegments geometry={edgeGeo} position={[0, 0, 0.02]}>
            <lineBasicMaterial color="#22c55e" />
          </lineSegments>
          {HANDLE_CORNERS.map(([sx, sy]) => (
            <Html key={`${sx}${sy}`} position={[sx * w / 2, sy * h / 2, 0.03]} center zIndexRange={[20, 10]} style={{ pointerEvents: 'none' }}>
              <div
                style={handleStyle}
                onPointerDown={(ev) => onHandlePointerDown(ev, sx, sy)}
                data-testid={`obj-handle-${obj.id}`}
              />
            </Html>
          ))}
          {/* Sits on the item, not above it, so it cannot drift off-canvas. */}
          <Html position={[0, 0, 0.03]} center zIndexRange={[20, 10]} style={{ pointerEvents: 'none' }}>
            <div style={{
              background: 'rgba(0,0,0,0.82)', color: '#22c55e', padding: '2px 8px', borderRadius: 4,
              fontSize: 11, fontWeight: 900, whiteSpace: 'nowrap', border: '1px solid rgba(34,197,94,0.4)',
            }}>
              {formatSize(w, h)}
            </div>
          </Html>
        </>
      )}
    </group>
  );
};

/** A free-floating panel of finish material pinned to a wall (e.g. a 4x8 wallpaper drop). */
const MaterialPatch = ({ patchData, surface, role }) => {
  const { roomCfg, lighting, selection, setSelection, update, setLiveLabel } = useContext(VizContext);
  const groupRef = useRef();
  const beginDrag = useWorldDrag();
  const { height_ft: H } = roomCfg;
  const t = wallTransform(role, roomCfg);

  const w = Math.max(0.25, patchData.w_ft || 4);
  const h = Math.max(0.25, patchData.h_ft || 8);
  const mat = patchData.material || {};
  const selected = selection?.kind === 'patch' && selection.id === patchData.id;
  const edgeGeo = useEdgeGeometry(w, h);

  const texture = useSurfaceTexture({
    imageUrl: mat.image || null,
    mode: mat.display_mode || 'sheet',
    pattern: mat.pattern || 'stacked_horizontal',
    tileCrop: mat.tile_crop || null,
    surfaceW: w,
    surfaceH: h,
    tileSize: mat.size || '',
    tileScale: mat.tile_scale || 1,
  });

  const patch = useCallback((changes) => {
    update((prev) => ({
      ...prev,
      surfaces: (prev.surfaces || []).map((s) => (s.id !== surface.id ? s : {
        ...s,
        patches: (s.patches || []).map((p) => (p.id === patchData.id ? { ...p, ...changes } : p)),
      })),
    }));
  }, [surface.id, patchData.id, update]);

  const onBodyPointerDown = (e) => {
    e.stopPropagation();
    setSelection({ kind: 'patch', id: patchData.id, surfaceId: surface.id });
    const parent = groupRef.current?.parent;
    if (!parent) return;
    const plane = anchorPlane('wall', role, roomCfg);
    const halfW = (t?.w || roomCfg.width_ft) / 2;

    beginDrag(plane, (worldHit) => {
      const local = parent.worldToLocal(worldHit.clone());
      patch({
        x_ft: clamp(local.x, -halfW + w / 2, halfW - w / 2),
        y_ft: clamp(local.y + H / 2, h / 2, H - h / 2),
      });
      setLiveLabel(formatSize(w, h));
    }, () => setLiveLabel(null));
  };

  const onHandlePointerDown = (ev, sx, sy) => {
    ev.stopPropagation();
    ev.preventDefault();
    const group = groupRef.current;
    if (!group) return;
    setSelection({ kind: 'patch', id: patchData.id, surfaceId: surface.id });
    const basis = captureBasis(group, sx, sy, w, h);
    const bounds = extentsFor('wall', role, roomCfg);

    beginDrag(basis.plane, (worldHit) => {
      const { newW, newH, local } = resolveResize(worldHit, basis, bounds);
      const halfW = bounds.maxW / 2;
      patch({
        w_ft: newW,
        h_ft: newH,
        x_ft: clamp(local.x, -halfW + newW / 2, halfW - newW / 2),
        y_ft: clamp(local.y + H / 2, newH / 2, H - newH / 2),
      });
      setLiveLabel(formatSize(newW, newH));
    }, () => setLiveLabel(null));
  };

  return (
    <group
      ref={groupRef}
      position={[patchData.x_ft || 0, (patchData.y_ft ?? H / 2) - H / 2, 0.02]}
      name={`patch:${patchData.id}`}
    >
      <mesh onPointerDown={onBodyPointerDown}>
        <planeGeometry args={[w, h]} />
        {texture ? (
          lighting === 'soft'
            ? <meshStandardMaterial key={`ps-${texture.id}`} map={texture} roughness={0.9} side={THREE.FrontSide} />
            : <meshBasicMaterial key={`pb-${texture.id}`} map={texture} side={THREE.FrontSide} />
        ) : (
          <meshBasicMaterial key="pb-plain" color={mat.swatch_color || '#b9b2a4'} side={THREE.FrontSide} />
        )}
      </mesh>

      {selected && (
        <>
          <lineSegments geometry={edgeGeo} position={[0, 0, 0.01]}>
            <lineBasicMaterial color="#f59e0b" />
          </lineSegments>
          {HANDLE_CORNERS.map(([sx, sy]) => (
            <Html key={`${sx}${sy}`} position={[sx * w / 2, sy * h / 2, 0.02]} center zIndexRange={[20, 10]} style={{ pointerEvents: 'none' }}>
              <div
                style={{ ...handleStyle, background: '#f59e0b' }}
                onPointerDown={(ev) => onHandlePointerDown(ev, sx, sy)}
                data-testid={`patch-handle-${patchData.id}`}
              />
            </Html>
          ))}
          {/* Sits on the panel, not above it, so it cannot drift off-canvas. */}
          <Html position={[0, 0, 0.02]} center zIndexRange={[20, 10]} style={{ pointerEvents: 'none' }}>
            <div style={{
              background: 'rgba(0,0,0,0.82)', color: '#f59e0b', padding: '2px 8px', borderRadius: 4,
              fontSize: 11, fontWeight: 900, whiteSpace: 'nowrap', border: '1px solid rgba(245,158,11,0.4)',
            }}>
              {formatSize(w, h)}
            </div>
          </Html>
        </>
      )}
    </group>
  );
};

/* ============================ wall ============================ */

const DEFAULT_ZONES = [{ id: 'main_wall', label: 'Main Wall', height: 85 }, { id: 'floor', label: 'Floor', height: 15 }];

/** A zone may not be squeezed below this share of the wall. */
const MIN_ZONE_PCT = 3;

export const ANNOTATION_COLORS = ['#22c55e', '#f59e0b', '#38bdf8', '#f472b6', '#ffffff'];

/**
 * A dimension annotation drawn on a surface, stored in that surface's local
 * feet so it stays welded to the model as the camera moves. On a wall that is
 * (x across, y above the floor); on the floor or ceiling it is (x across the
 * width, y along the depth). The distance is measured from the room's real geometry; setting
 * `label` overrides the text.
 */
const Annotation = ({ ann, role, faceW, faceH }) => {
  const { roomCfg, selection, setSelection, update, setLiveLabel } = useContext(VizContext);
  const beginDrag = useWorldDrag();
  const groupRef = useRef();
  const [editing, setEditing] = useState(false);
  const selected = selection?.kind === 'annotation' && selection.id === ann.id;

  const ax = ann.ax, ay = ann.ay, bx = ann.bx, by = ann.by;
  const dx = bx - ax;
  const dy = by - ay;
  const len = Math.hypot(dx, dy);
  const angle = Math.atan2(dy, dx);
  const mx = (ax + bx) / 2;
  const my = (ay + by) / 2;
  const color = ann.color || ANNOTATION_COLORS[0];
  const text = ann.label && ann.label.trim() ? ann.label : formatFeet(len);

  const patch = useCallback((changes) => {
    update((prev) => ({
      ...prev,
      annotations: (prev.annotations || []).map((a) => (a.id === ann.id ? { ...a, ...changes } : a)),
    }));
  }, [ann.id, update]);

  const dragEnd = (which) => (e) => {
    e.stopPropagation();
    e.preventDefault?.();
    setSelection({ kind: 'annotation', id: ann.id });
    const parent = groupRef.current?.parent;
    if (!parent) return;
    // Floors and ceilings are horizontal planes, not walls; anchorPlane knows
    // both already, so the anchor comes from the role this annotation sits on.
    const anchor = (role === 'floor' || role === 'ceiling') ? role : 'wall';
    const plane = anchorPlane(anchor, role, roomCfg);

    beginDrag(plane, (worldHit) => {
      const local = parent.worldToLocal(worldHit.clone());
      const nx = clamp(local.x, -faceW / 2, faceW / 2);
      const ny = clamp(local.y + faceH / 2, 0, faceH);
      patch(which === 'a' ? { ax: nx, ay: ny } : { bx: nx, by: ny });
      const p = which === 'a' ? { x: nx, y: ny } : { x: ax, y: ay };
      const q = which === 'a' ? { x: bx, y: by } : { x: nx, y: ny };
      setLiveLabel(formatFeet(Math.hypot(q.x - p.x, q.y - p.y)));
    }, () => setLiveLabel(null));
  };

  const dot = {
    width: 11, height: 11, borderRadius: '50%', background: color,
    border: '2px solid rgba(0,0,0,0.75)', cursor: 'move', pointerEvents: 'auto',
    boxShadow: '0 1px 4px rgba(0,0,0,0.6)',
  };

  return (
    <group ref={groupRef} name={`annot:${ann.id}`}>
      {/* measure line */}
      <mesh position={[mx, my - faceH / 2, 0.06]} rotation={[0, 0, angle]}
        onPointerDown={(e) => { e.stopPropagation(); setSelection({ kind: 'annotation', id: ann.id }); }}>
        <planeGeometry args={[Math.max(len, 0.01), selected ? 0.05 : 0.03]} />
        <meshBasicMaterial color={color} />
      </mesh>
      {/* end ticks */}
      {[[ax, ay], [bx, by]].map(([px, py], i) => (
        <mesh key={i} position={[px, py - faceH / 2, 0.06]} rotation={[0, 0, angle + Math.PI / 2]}>
          <planeGeometry args={[0.35, 0.03]} />
          <meshBasicMaterial color={color} />
        </mesh>
      ))}

      {/* value — click to override, blank the field to go back to measured */}
      <Html position={[mx, my - faceH / 2 + 0.22, 0.07]} center zIndexRange={[24, 12]} style={{ pointerEvents: 'none' }}>
        {editing ? (
          <input
            autoFocus
            defaultValue={ann.label || ''}
            placeholder={formatFeet(len)}
            onBlur={(e) => { patch({ label: e.target.value }); setEditing(false); }}
            onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); if (e.key === 'Escape') setEditing(false); }}
            data-testid={`annot-input-${ann.id}`}
            style={{
              pointerEvents: 'auto', width: 86, textAlign: 'center', fontSize: 11, fontWeight: 800,
              background: '#000', color, border: `1px solid ${color}`, borderRadius: 4, padding: '2px 4px',
            }}
          />
        ) : (
          <div
            onPointerDown={(e) => { e.stopPropagation(); setSelection({ kind: 'annotation', id: ann.id }); setEditing(true); }}
            data-testid={`annot-label-${ann.id}`}
            title="Click to type your own value"
            style={{
              pointerEvents: 'auto', cursor: 'text', whiteSpace: 'nowrap',
              background: 'rgba(0,0,0,0.82)', color, padding: '2px 8px', borderRadius: 4,
              fontSize: 11, fontWeight: 900, border: `1px solid ${color}66`,
            }}
          >
            {text}{ann.label && ann.label.trim() ? '' : ''}
          </div>
        )}
      </Html>

      {/* draggable endpoints */}
      {[['a', ax, ay], ['b', bx, by]].map(([which, px, py]) => (
        <Html key={which} position={[px, py - faceH / 2, 0.08]} center zIndexRange={[24, 12]} style={{ pointerEvents: 'none' }}>
          <div style={dot} onPointerDown={dragEnd(which)} data-testid={`annot-${which}-${ann.id}`} />
        </Html>
      ))}
    </group>
  );
};

/**
 * Invisible click-catcher over a wall, used only while measuring. It sits in
 * front of existing annotations so a new measurement can be started across one.
 */
const MeasureCapture = ({ faceW, faceH, onPick }) => (
  <mesh
    position={[0, 0, 0.15]}
    onPointerDown={(e) => {
      e.stopPropagation();
      const p = e.point.clone();
      const local = e.object.parent.worldToLocal(p);
      onPick({ x: clamp(local.x, -faceW / 2, faceW / 2), y: clamp(local.y + faceH / 2, 0, faceH) });
    }}
  >
    <planeGeometry args={[faceW, faceH]} />
    <meshBasicMaterial transparent opacity={0} depthWrite={false} />
  </mesh>
);

/**
 * Wainscot / trim boundaries run level around a room, so a drag on one wall
 * applies to every wall — matching what the Add Wainscot button already does.
 */
function applyZonesToWalls(update, nextZones) {
  update((prev) => ({
    ...prev,
    surfaces: (prev.surfaces || []).map((s) => (
      s.surface_type === 'wall' ? { ...s, zone_config: nextZones } : s
    )),
  }));
}

/**
 * The trim line between two finish zones, draggable up and down the wall.
 * Dragging moves height between the zone above and the zone below, so the wall
 * always adds up to 100%.
 */
const ZoneDivider = ({ role, zones, index, faceW, faceH }) => {
  const { roomCfg, update, setLiveLabel } = useContext(VizContext);
  const beginDrag = useWorldDrag();
  const groupRef = useRef();
  const [hot, setHot] = useState(false);

  const accTop = zones.slice(0, index + 1).reduce((s, z) => s + (z.height || 0), 0);
  const y = faceH / 2 - (accTop / 100) * faceH;

  const onPointerDown = (e) => {
    e.stopPropagation();
    const parent = groupRef.current?.parent;
    if (!parent) return;
    const plane = anchorPlane('wall', role, roomCfg);
    const before = zones.slice(0, index).reduce((s, z) => s + (z.height || 0), 0);
    const pairTotal = (zones[index].height || 0) + (zones[index + 1].height || 0);

    beginDrag(plane, (worldHit) => {
      const local = parent.worldToLocal(worldHit.clone());
      const acc = clamp(
        ((faceH / 2 - local.y) / faceH) * 100,
        before + MIN_ZONE_PCT,
        before + pairTotal - MIN_ZONE_PCT,
      );
      const topHeight = acc - before;
      const next = zones.map((z, i) => (
        i === index ? { ...z, height: topHeight }
          : i === index + 1 ? { ...z, height: pairTotal - topHeight }
            : z
      ));
      applyZonesToWalls(update, next);
      setLiveLabel(`${zones[index + 1].label || 'Trim'} top — ${formatFeet((1 - acc / 100) * roomCfg.height_ft)} A.F.F.`);
    }, () => setLiveLabel(null));
  };

  return (
    <group ref={groupRef} position={[0, y, 0.012]}>
      {/* visible trim */}
      <mesh>
        <planeGeometry args={[faceW, hot ? 0.07 : 0.04]} />
        <meshBasicMaterial color={hot ? '#D4A574' : '#999'} />
      </mesh>
      {/* Invisible grab strip, deliberately taller than the trim so it is easy
          to catch with a mouse (~5in of wall either side of the line). */}
      <mesh
        onPointerDown={onPointerDown}
        onPointerOver={() => { setHot(true); document.body.style.cursor = 'ns-resize'; }}
        onPointerOut={() => { setHot(false); document.body.style.cursor = 'auto'; }}
      >
        <planeGeometry args={[faceW, 0.8]} />
        <meshBasicMaterial transparent opacity={0} depthWrite={false} />
      </mesh>
    </group>
  );
};

/** Human name for each surface, used on the in-scene nameplates. */
const SURFACE_NAMES = {
  back: 'BACK WALL', left: 'LEFT WALL', right: 'RIGHT WALL', front: 'FRONT WALL',
  floor: 'FLOOR', ceiling: 'CEILING',
};

/** Has any zone of this surface actually been given a finish? */
function surfaceHasFinish(surface) {
  return (surface?.materials || []).some((m) => m && (m.image || m.swatch_color));
}

/**
 * Name badge floating on a surface, so opening a room immediately shows which
 * wall is which and where a finish can go. It steps aside once that surface has
 * a finish, and reappears for every surface while a finish is being dragged.
 */
const SurfaceNameplate = ({ role, faceW, faceH, applied, offset = 0.05 }) => {
  const { dragging, showLabels, measureMode, dropTarget } = useContext(VizContext);
  // Is the pointer currently over THIS surface mid-drag?
  const isTarget = dropTarget?.kind === 'surface'
    && (dropTarget.id === `${role}_wall` || dropTarget.id === role);

  if (!showLabels && !isTarget) return null;
  // Out of the way while measuring: the badge is a DOM overlay and would
  // otherwise swallow the two clicks that place a dimension.
  if (measureMode) return null;
  if (applied && !dragging && !isTarget) return null;

  return (
    <Html
      position={[0, faceH ? 0 : 0, offset]}
      center
      zIndexRange={[18, 8]}
      style={{ pointerEvents: 'none' }}
    >
      <div
        data-testid={`surface-label-${role}`}
        data-target={isTarget ? "true" : "false"}
        style={{
          padding: isTarget ? '5px 12px' : '3px 8px',
          borderRadius: 5,
          background: isTarget
            ? 'rgba(34,197,94,0.96)'
            : (applied ? 'rgba(0,0,0,0.55)' : 'rgba(212,165,116,0.92)'),
          color: isTarget ? '#fff' : (applied ? '#D4A574' : '#111'),
          border: isTarget
            ? '2px solid #16a34a'
            : (applied ? '1px solid rgba(212,165,116,0.5)' : '1px solid rgba(0,0,0,0.35)'),
          boxShadow: isTarget ? '0 0 18px rgba(34,197,94,0.85)' : 'none',
          font: isTarget ? '900 12px/1.15 system-ui, sans-serif' : '900 10px/1.15 system-ui, sans-serif',
          letterSpacing: 1,
          textAlign: 'center',
          whiteSpace: 'nowrap',
          transform: isTarget ? 'scale(1.06)' : 'none',
        }}
      >
        {SURFACE_NAMES[role] || role.toUpperCase()}
        {isTarget ? (
          <div style={{ font: '800 8px/1.3 system-ui, sans-serif', letterSpacing: 0.5 }}>
            RELEASE TO APPLY
          </div>
        ) : !applied && (
          <div style={{ font: '700 7px/1.3 system-ui, sans-serif', opacity: 0.75, letterSpacing: 0.5 }}>
            DROP FINISH HERE
          </div>
        )}
      </div>
    </Html>
  );
};

const WallFace = ({ role, surface, objects, annotations }) => {
  const { roomCfg, lighting, measureMode, measureTarget, onMeasurePick } = useContext(VizContext);
  const t = wallTransform(role, roomCfg);
  if (!t || !surface) return null;

  const wallAnnotations = (annotations || []).filter((a) => a.surface === role);

  const zones = surface.zone_config?.length ? surface.zone_config : DEFAULT_ZONES;
  const wallObjects = (objects || []).filter((o) => o.anchor === 'wall' && o.surface_id === surface.id);

  return (
    <group position={t.position} rotation={t.rotation} name={`${role}_wall`}>
      {zones.map((zone, i) => {
        const material = getMaterialFor(surface, zone.id);
        const zoneH = t.h * (zone.height / 100);
        let accTop = 0;
        for (let j = 0; j < i; j++) accTop += zones[j].height;
        const yCenter = t.h / 2 - ((accTop + zone.height / 2) / 100) * t.h;
        return (
          <ZoneFace
            key={zone.id}
            material={material}
            width={t.w}
            height={zoneH}
            y={yCenter}
            lighting={lighting}
          />
        );
      })}

      {/* Trim between zones — drag to move the wainscot/base height */}
      {zones.length > 1 && zones.slice(0, -1).map((zone, i) => (
        <ZoneDivider key={`trim-${zone.id}`} role={role} zones={zones} index={i} faceW={t.w} faceH={t.h} />
      ))}

      <SurfaceNameplate role={role} faceW={t.w} faceH={t.h} applied={surfaceHasFinish(surface)} />

      {wallAnnotations.map((a) => (
        <Annotation key={a.id} ann={a} role={role} faceW={t.w} faceH={t.h} />
      ))}
      {measureMode && measureTarget === 'auto' && (
        <MeasureCapture faceW={t.w} faceH={t.h} onPick={(pt) => onMeasurePick(role, pt)} />
      )}

      {(surface.patches || []).map((p) => (
        <MaterialPatch key={p.id} patchData={p} surface={surface} role={role} />
      ))}
      {wallObjects.map((o) => (
        <PlacedObject key={o.id} obj={o} role={role} />
      ))}
    </group>
  );
};

/* ============================ shower preset extras ============================ */

const goldMat = { color: '#c8a84e', roughness: 0.25, metalness: 0.85 };

const ShowerFixtures = ({ W, H, D }) => (
  <group name="shower_fixtures">
    <group position={[0, H - 0.05, -D / 2 + 0.15]}>
      <mesh><cylinderGeometry args={[0.02, 0.02, 0.6, 8]} /><meshStandardMaterial {...goldMat} /></mesh>
      <mesh position={[0, -0.35, 0]}><cylinderGeometry args={[0.2, 0.22, 0.06, 24]} /><meshStandardMaterial {...goldMat} /></mesh>
    </group>
    <group position={[-W / 2 + 0.15, H * 0.75, -D * 0.15]}>
      <mesh rotation={[0, 0, Math.PI / 6]}><cylinderGeometry args={[0.02, 0.02, 0.5, 8]} /><meshStandardMaterial {...goldMat} /></mesh>
      <mesh position={[0.2, 0.15, 0]}><sphereGeometry args={[0.12, 16, 16]} /><meshStandardMaterial {...goldMat} /></mesh>
    </group>
    <group position={[-W / 2 + 0.15, H * 0.55, -D * 0.15]}>
      <mesh><cylinderGeometry args={[0.015, 0.015, 2.2, 8]} /><meshStandardMaterial {...goldMat} /></mesh>
      <mesh position={[0, 0.3, 0.08]}><cylinderGeometry args={[0.06, 0.05, 0.15, 12]} /><meshStandardMaterial {...goldMat} /></mesh>
    </group>
    <group position={[W / 2 - 0.08, H * 0.5, 0]}>
      <mesh rotation={[Math.PI / 2, 0, 0]}><cylinderGeometry args={[0.1, 0.1, 0.03, 24]} /><meshStandardMaterial {...goldMat} /></mesh>
    </group>
    <mesh position={[0, 0.02, 0.3]} rotation={[-Math.PI / 2, 0, 0]}>
      <cylinderGeometry args={[0.06, 0.06, 0.01, 16]} /><meshStandardMaterial {...goldMat} />
    </mesh>
  </group>
);

const Niche = ({ position, nicheW, nicheH, nicheD = 0.3, imageUrl, tileCrop, tileSize = '' }) => {
  const texture = useSurfaceTexture({
    imageUrl, mode: 'sheet', pattern: 'stacked_horizontal', tileCrop,
    // tileSize keeps the recess tiled at the same physical scale as the wall;
    // without it a single tile gets stretched across the whole niche.
    surfaceW: nicheW, surfaceH: nicheH, tileSize,
  });
  return (
    <group position={position} name="niche">
      <mesh position={[0, 0, -0.005]}>
        <planeGeometry args={[nicheW, nicheH]} />
        {/* keys force a fresh material when the map arrives — adding `map` to an
            existing material leaves the compiled shader without a map sampler */}
        {texture
          ? <meshBasicMaterial key={`n-${texture.id}`} map={texture} color="#8a8a8a" side={THREE.FrontSide} />
          : <meshBasicMaterial key="n-plain" color="#111111" side={THREE.FrontSide} />}
      </mesh>
      <mesh position={[0, nicheH / 2, nicheD / 2]} rotation={[Math.PI / 2, 0, 0]}>
        <planeGeometry args={[nicheW, nicheD]} /><meshBasicMaterial color="#080808" side={THREE.FrontSide} />
      </mesh>
      <mesh position={[0, -nicheH / 2, nicheD / 2]} rotation={[-Math.PI / 2, 0, 0]}>
        <planeGeometry args={[nicheW, nicheD]} /><meshBasicMaterial color="#1a1a1a" side={THREE.FrontSide} />
      </mesh>
      <mesh position={[-nicheW / 2, 0, nicheD / 2]} rotation={[0, Math.PI / 2, 0]}>
        <planeGeometry args={[nicheD, nicheH]} /><meshBasicMaterial color="#0e0e0e" side={THREE.FrontSide} />
      </mesh>
      <mesh position={[nicheW / 2, 0, nicheD / 2]} rotation={[0, -Math.PI / 2, 0]}>
        <planeGeometry args={[nicheD, nicheH]} /><meshBasicMaterial color="#0e0e0e" side={THREE.FrontSide} />
      </mesh>
    </group>
  );
};

const Bench = ({ W, D, benchW, benchH, side, imageUrl, tileCrop, tileSize = '' }) => {
  const texture = useSurfaceTexture({
    imageUrl, mode: 'sheet', pattern: 'stacked_horizontal', tileCrop,
    surfaceW: benchW, surfaceH: benchH, tileSize,
  });
  const benchD = D * 0.85;
  const x = side === 'right' ? W / 2 - benchW / 2 : -W / 2 + benchW / 2;
  return (
    <group position={[x, benchH / 2, -D / 2 + benchD / 2]} name="bench">
      <mesh>
        <boxGeometry args={[benchW, benchH, benchD]} />
        {texture
          ? <meshBasicMaterial key={`bx-${texture.id}`} map={texture} color="#ffffff" />
          : <meshStandardMaterial key="bx-plain" color="#e8e0d8" roughness={0.3} metalness={0.05} />}
      </mesh>
      <mesh position={[0, benchH / 2 + 0.005, 0]} rotation={[-Math.PI / 2, 0, 0]}>
        <planeGeometry args={[benchW, benchD]} />
        {texture
          ? <meshBasicMaterial key={`bt-${texture.id}`} map={texture} color="#ffffff" />
          : <meshStandardMaterial key="bt-plain" color="#f0ece6" roughness={0.2} metalness={0.02} />}
      </mesh>
    </group>
  );
};

/* ============================ scene ============================ */

function SceneRefCapture({ sceneRefObj }) {
  const { camera, scene, gl } = useThree();
  useEffect(() => { sceneRefObj.current = { camera, scene, gl }; }, [camera, scene, gl, sceneRefObj]);
  return null;
}

/** Keeps the camera framed on the room whenever its dimensions change. */
function CameraRig({ roomCfg, planView }) {
  const { camera, invalidate } = useThree();
  const { width_ft: W, depth_ft: D, height_ft: H } = roomCfg;
  useEffect(() => {
    if (planView) {
      // Straight down over the middle, far enough back to fit the whole
      // footprint. The nudge is along +z only: offsetting x and z equally left
      // the plan rotated 45 degrees, so the room sat diagonally on screen.
      camera.position.set(0, Math.max(W, D) * 1.5 + H * 0.5, 0.001);
    } else {
      camera.position.set(0, H * 0.48, D * 1.15 + Math.max(0, (W - D) * 0.15));
    }
    camera.updateProjectionMatrix();
    invalidate();
  }, [W, D, H, camera, invalidate, planView]);
  return null;
}

const Scene = ({ schedule, sceneRef, dragging }) => {
  const { roomCfg, lighting, measureMode, measureTarget, onMeasurePick, planView } = useContext(VizContext);
  const { width_ft: W, depth_ft: D, height_ft: H } = roomCfg;
  const surfaces = schedule?.surfaces || [];
  const objects = schedule?.objects || [];
  const annotations = schedule?.annotations || [];
  const roles = useMemo(() => getSurfaceRoles(surfaces), [surfaces]);
  const isShower = roomCfg.preset === 'shower';

  const floorMat = getMaterialFor(roles.floor, '_surface');
  const ceilMat = getMaterialFor(roles.ceiling, '_surface');

  const floorTex = useSurfaceTexture({
    imageUrl: floorMat?.image || null,
    mode: floorMat?.display_mode || 'sheet',
    pattern: floorMat?.pattern || 'stacked_horizontal',
    tileCrop: floorMat?.tile_crop || null,
    surfaceW: W, surfaceH: D,
    tileSize: floorMat?.size || '',
    tileScale: floorMat?.tile_scale || 1,
  });
  const ceilTex = useSurfaceTexture({
    imageUrl: ceilMat?.image || null,
    mode: ceilMat?.display_mode || 'full',
    pattern: ceilMat?.pattern || 'stacked_horizontal',
    tileCrop: ceilMat?.tile_crop || null,
    surfaceW: W, surfaceH: D,
    tileSize: ceilMat?.size || '',
    tileScale: ceilMat?.tile_scale || 1,
  });

  const backWall = roles.back;
  const backMat = getMaterialFor(backWall, 'main_wall');
  const niches = backWall?.niches || [];
  const benches = backWall?.benches || [];
  const wallCount = roomCfg.wall_count || 3;
  const floorObjects = objects.filter((o) => o.anchor !== 'wall');

  return (
    <>
      <SceneRefCapture sceneRefObj={sceneRef} />
      <CameraRig roomCfg={roomCfg} planView={planView} />

      <ambientLight intensity={lighting === 'soft' ? 0.65 : 0.9} color="#ffffff" />
      <pointLight position={[0, H - 0.2, D * 0.2]} intensity={lighting === 'soft' ? 1.6 : 1.2} distance={Math.max(20, W * 3)} decay={2} />
      <directionalLight position={[W * 0.3, H, D]} intensity={lighting === 'soft' ? 0.85 : 0.5} />

      <WallFace role="back" surface={roles.back} objects={objects} annotations={annotations} />
      <WallFace role="left" surface={roles.left} objects={objects} annotations={annotations} />
      <WallFace role="right" surface={roles.right} objects={objects} annotations={annotations} />
      {wallCount >= 4 && <WallFace role="front" surface={roles.front} objects={objects} annotations={annotations} />}

      <SurfaceFace
        name="floor"
        position={[0, 0.01, 0]}
        rotation={[-Math.PI / 2, 0, 0]}
        size={[W, D]}
        texture={floorTex}
        color={floorMat?.swatch_color || '#4a4a4a'}
        lighting={lighting}
      />
      <SurfaceFace
        name="ceiling"
        position={[0, H, 0]}
        rotation={[Math.PI / 2, 0, 0]}
        size={[W, D]}
        texture={ceilTex}
        color={ceilMat?.swatch_color || '#c5c0b8'}
        lighting={lighting}
      />

      {/* The floor and ceiling take measurements the same way walls do. These
          groups are already rotated into the plane, so an annotation's local
          feet need no special casing - only the drag plane differs. */}
      <group position={[0, 0.02, 0]} rotation={[-Math.PI / 2, 0, 0]} name="floor_face">
        <SurfaceNameplate role="floor" faceW={W} faceH={D} applied={surfaceHasFinish(roles.floor)} />
        {annotations.filter((a) => a.surface === 'floor').map((a) => (
          <Annotation key={a.id} ann={a} role="floor" faceW={W} faceH={D} />
        ))}
        {measureMode && (measureTarget === 'floor' || measureTarget === 'auto') && (
          <MeasureCapture faceW={W} faceH={D} onPick={(pt) => onMeasurePick('floor', pt)} />
        )}
      </group>
      <group position={[0, H - 0.02, 0]} rotation={[Math.PI / 2, 0, 0]} name="ceiling_face">
        <SurfaceNameplate role="ceiling" faceW={W} faceH={D} applied={surfaceHasFinish(roles.ceiling)} />
        {annotations.filter((a) => a.surface === 'ceiling').map((a) => (
          <Annotation key={a.id} ann={a} role="ceiling" faceW={W} faceH={D} />
        ))}
        {measureMode && (measureTarget === 'ceiling' || measureTarget === 'auto') && (
          <MeasureCapture faceW={W} faceH={D} onPick={(pt) => onMeasurePick('ceiling', pt)} />
        )}
      </group>

      {floorObjects.map((o) => <PlacedObject key={o.id} obj={o} role="floor" />)}

      {isShower && (
        <>
          {niches.length > 0
            ? niches.map((n, i) => {
              const nx = ((n.x ?? 35) / 100 - 0.5) * W;
              const nw = Math.max(0.3, ((n.w ?? 30) / 100) * W);
              const nh = Math.max(0.3, ((n.h ?? 25) / 100) * H);
              const ny = H - ((n.y ?? 30) / 100) * H - nh / 2;
              const nm = n.material?.image ? n.material : backMat;
              return <Niche key={n.id || i} position={[nx, ny, -D / 2 + 0.05]} nicheW={nw} nicheH={nh}
                imageUrl={nm?.image} tileCrop={nm?.tile_crop} tileSize={nm?.size || ''} />;
            })
            : <Niche position={[0, H * 0.58, -D / 2 + 0.05]} nicheW={1.3} nicheH={0.9}
              imageUrl={backMat?.image} tileSize={backMat?.size || ''} />}

          {benches.length > 0
            ? benches.map((b, i) => (
              <Bench
                key={b.id || i}
                W={W} D={D}
                benchW={Math.max(0.5, ((b.w ?? 20) / 100) * W)}
                benchH={Math.max(0.5, ((b.h ?? 22) / 100) * H)}
                side={(b.x ?? 60) > 50 ? 'right' : 'left'}
                imageUrl={b.material?.image}
                tileCrop={b.material?.tile_crop}
                tileSize={b.material?.size || ''}
              />
            ))
            : <Bench W={W} D={D} benchW={1} benchH={1.8} side="right" />}

          <ShowerFixtures W={W} H={H} D={D} />
        </>
      )}

      {/* Corner shadow strips — cheap ambient occlusion that reads as depth */}
      {[[-W / 2 + 0.005, -D / 2 + 0.005, 0.35], [W / 2 - 0.005, -D / 2 + 0.005, 0.35],
        [-W / 2 + 0.005, D / 2 - 0.005, 0.15], [W / 2 - 0.005, D / 2 - 0.005, 0.15]].map(([x, z, op], i) => (
        <mesh key={`corner-${i}`} position={[x, H / 2, z]}>
          <boxGeometry args={[0.02, H, 0.02]} />
          <meshBasicMaterial color="#000" transparent opacity={op} />
        </mesh>
      ))}

      {/* In plan view the camera has to be able to point straight down, and it
          orbits the floor rather than the back wall. */}
      <OrbitControls
        enabled={!dragging}
        enablePan={false}
        enableZoom
        minPolarAngle={planView ? 0 : Math.PI * 0.2}
        maxPolarAngle={planView ? Math.PI * 0.35 : Math.PI * 0.65}
        minAzimuthAngle={planView ? -Infinity : -Math.PI * 0.3}
        maxAzimuthAngle={planView ? Infinity : Math.PI * 0.3}
        target={planView ? [0, 0, 0] : [0, H * 0.42, -D / 2]}
        minDistance={Math.max(2, D * 0.6)}
        maxDistance={planView ? Math.max(W, D) * 3 + H : D * 2.2 + W}
      />
    </>
  );
};

/* ============================ overlay UI ============================ */

const DimInput = ({ label, value, onCommit, testId }) => {
  const [draft, setDraft] = useState('');
  const [editing, setEditing] = useState(false);
  const shown = editing ? draft : formatFeet(value, { compact: true });
  return (
    <label className="flex items-center gap-1">
      <span className="text-[9px] font-bold text-white/40">{label}</span>
      <input
        data-testid={testId}
        value={shown}
        onFocus={() => { setEditing(true); setDraft(formatFeet(value, { compact: true })); }}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={() => {
          setEditing(false);
          const parsed = parseFeet(draft, null);
          if (parsed !== null && parsed > 0) onCommit(parsed);
        }}
        onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
        className="w-16 px-1.5 py-1 rounded bg-black/60 border border-white/15 text-white text-[10px] font-bold"
      />
    </label>
  );
};

/* ============================ main component ============================ */

const RoomVisualizer3D = ({
  schedule,
  onDropTile,
  onUpdateSchedule,
  selectedItem = null,
  height = 720,
}) => {
  const sceneRef = useRef(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [dropTarget, setDropTarget] = useState(null);   // surface under the pointer mid-drag
  const [dragging, setDragging] = useState(false);
  const [selection, setSelection] = useState(null);
  const [liveLabel, setLiveLabel] = useState(null);
  const [objectMode, setObjectMode] = useState(false);
  const [objectType, setObjectType] = useState('sofa');
  const [measureMode, setMeasureMode] = useState(false);
  // Which surface a measuring click lands on. On a tall room the floor is only
  // a sliver of the view - measured at the bottom 3% of the canvas - so
  // "whatever is nearest" makes floor and ceiling dimensions impractical to
  // place. Choosing the target makes every click land where it is meant to.
  const [measureTarget, setMeasureTarget] = useState('auto');   // auto | floor | ceiling
  // Looking straight down. The floor is barely visible from the room view, so
  // measuring it needs a plan view - which is what was asked for.
  const [planView, setPlanView] = useState(false);
  // Wall nameplates are on by default: opening a room should show which
  // wall is which without hunting for it.
  const [showLabels, setShowLabels] = useState(true);
  // First click of a measurement. Held in a ref, not state: React invokes state
  // updaters twice in dev, so doing this work inside one would double-fire.
  const pendingRef = useRef(null);
  const dragTimerRef = useRef(null);

  const roomCfg = useMemo(() => ({
    ...(schedule?.schedule_type === 'tile' ? SHOWER_ROOM_CONFIG : DEFAULT_ROOM_CONFIG),
    ...(schedule?.room_config || {}),
  }), [schedule?.room_config, schedule?.schedule_type]);

  const lighting = roomCfg.lighting || 'flat';

  const update = useCallback((updater) => {
    if (onUpdateSchedule) onUpdateSchedule(updater);
  }, [onUpdateSchedule]);

  const setRoom = useCallback((changes) => {
    update((prev) => ({ ...prev, room_config: { ...roomCfg, ...changes } }));
  }, [update, roomCfg]);

  /**
   * Measuring takes two clicks on the same wall. The distance is derived from
   * the room's real dimensions, so no typing is needed unless you want to
   * override the text.
   */
  const onMeasurePick = useCallback((role, pt) => {
    const first = pendingRef.current;
    if (!first || first.surface !== role) {
      pendingRef.current = { surface: role, ...pt };
      setLiveLabel('Click the second point');
      return;
    }
    const ann = {
      id: crypto.randomUUID(),
      surface: role,
      ax: first.x, ay: first.y,
      bx: pt.x, by: pt.y,
      label: '',
      color: ANNOTATION_COLORS[0],
    };
    pendingRef.current = null;
    update((prev) => ({ ...prev, annotations: [...(prev.annotations || []), ann] }));
    setSelection({ kind: 'annotation', id: ann.id });
    setLiveLabel(null);
  }, [update]);

  const ctx = useMemo(() => ({
    roomCfg, lighting, selection, setSelection, setDragging, update, setLiveLabel,
    measureMode, measureTarget, onMeasurePick, dragging, showLabels, dropTarget, planView,
  }), [roomCfg, lighting, selection, update, measureMode, measureTarget, onMeasurePick, dragging, showLabels, dropTarget, planView]);

  /* ---- add / remove ---- */

  const addObject = useCallback((typeKey, item) => {
    const preset = PRESET_BY_KEY[typeKey];
    if (!preset || !schedule) return;
    const roles = getSurfaceRoles(schedule.surfaces || []);
    const wallSurface = roles.back || roles.left || roles.right;

    const obj = {
      id: crypto.randomUUID(),
      type: typeKey,
      item_id: item?.id || null,
      name: item?.name || preset.label,
      image: item?._img || '',
      anchor: preset.anchor,
      surface_id: preset.anchor === 'wall' ? (wallSurface?.id || null) : null,
      x: 0,
      y: preset.y ?? null,
      z: preset.anchor === 'wall' ? null : -roomCfg.depth_ft * 0.15,
      w: preset.w,
      h: preset.h,
      rot_y: 0,
      cutout: preset.cutout,
      frame: !!preset.frame,
    };
    update((prev) => ({ ...prev, objects: [...(prev.objects || []), obj] }));
    setSelection({ kind: 'object', id: obj.id });
  }, [schedule, roomCfg.depth_ft, update]);

  const addPatch = useCallback((item) => {
    if (!schedule) return;
    const roles = getSurfaceRoles(schedule.surfaces || []);
    const target = roles.back || roles.left || roles.right;
    if (!target) return;
    const p = {
      id: crypto.randomUUID(),
      x_ft: 0,
      y_ft: roomCfg.height_ft / 2,
      w_ft: 4,
      h_ft: Math.min(8, roomCfg.height_ft),
      material: item
        ? { item_id: item.id, name: item.name || '', image: item._img || '', size: item.size || '', pattern: 'stacked_horizontal', display_mode: 'sheet', tile_scale: 1 }
        : { swatch_color: PAINT_SWATCHES[0].hex, name: PAINT_SWATCHES[0].name },
    };
    update((prev) => ({
      ...prev,
      surfaces: (prev.surfaces || []).map((s) => (s.id === target.id ? { ...s, patches: [...(s.patches || []), p] } : s)),
    }));
    setSelection({ kind: 'patch', id: p.id, surfaceId: target.id });
  }, [schedule, roomCfg.height_ft, update]);

  const deleteSelection = useCallback(() => {
    if (!selection) return;
    if (selection.kind === 'annotation') {
      update((prev) => ({
        ...prev,
        annotations: (prev.annotations || []).filter((a) => a.id !== selection.id),
      }));
      setSelection(null);
      return;
    }
    if (selection.kind === 'object') {
      update((prev) => ({ ...prev, objects: (prev.objects || []).filter((o) => o.id !== selection.id) }));
    } else {
      update((prev) => ({
        ...prev,
        surfaces: (prev.surfaces || []).map((s) => (s.id !== selection.surfaceId ? s : {
          ...s, patches: (s.patches || []).filter((p) => p.id !== selection.id),
        })),
      }));
    }
    setSelection(null);
  }, [selection, update]);

  useEffect(() => {
    const onKey = (e) => {
      const typing = /^(INPUT|TEXTAREA|SELECT)$/.test(e.target?.tagName || '');
      if ((e.key === 'Delete' || e.key === 'Backspace') && selection && !typing) {
        e.preventDefault();
        deleteSelection();
      }
      if (e.key === 'Escape') setSelection(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [selection, deleteSelection]);

  /* ---- palette drag & drop onto the scene ---- */

  const SURFACE_NAMES_HIT = ['back_wall', 'left_wall', 'right_wall', 'front_wall', 'floor', 'ceiling', 'bench', 'niche'];

  /** Which surface (if any) sits under the pointer right now. */
  const surfaceAtPointer = useCallback((clientX, clientY) => {
    if (!sceneRef.current) return null;
    const { camera, scene, gl } = sceneRef.current;
    const rect = gl.domElement.getBoundingClientRect();
    const mouse = new THREE.Vector2(
      ((clientX - rect.left) / rect.width) * 2 - 1,
      -((clientY - rect.top) / rect.height) * 2 + 1,
    );
    const raycaster = new THREE.Raycaster();
    raycaster.setFromCamera(mouse, camera);
    for (const hit of raycaster.intersectObjects(scene.children, true)) {
      let obj = hit.object;
      while (obj) {
        if (obj.name && obj.name.startsWith('obj:')) return { kind: 'object', id: obj.name.slice(4) };
        if (obj.name && obj.name.startsWith('patch:')) return { kind: 'patch', id: obj.name.slice(6) };
        if (obj.name && SURFACE_NAMES_HIT.includes(obj.name)) return { kind: 'surface', id: obj.name };
        obj = obj.parent;
      }
    }
    return null;
  }, []);

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
    setIsDragOver(true);
    // Work out the target as the pointer moves, so the wall about to receive
    // the finish lights up before the mouse is released.
    setDropTarget(surfaceAtPointer(e.clientX, e.clientY));
    clearTimeout(dragTimerRef.current);
    dragTimerRef.current = setTimeout(() => { setIsDragOver(false); setDropTarget(null); }, 200);
  };

  const applyToObject = (objId, item) => {
    update((prev) => ({
      ...prev,
      objects: (prev.objects || []).map((o) => (o.id === objId
        ? { ...o, image: item._img || o.image, item_id: item.id, name: item.name || o.name }
        : o)),
    }));
  };

  const applyToPatch = (patchId, item) => {
    update((prev) => ({
      ...prev,
      surfaces: (prev.surfaces || []).map((s) => ({
        ...s,
        patches: (s.patches || []).map((p) => (p.id !== patchId ? p : {
          ...p,
          material: { ...(p.material || {}), item_id: item.id, name: item.name || '', image: item._img || '', size: item.size || '', swatch_color: '' },
        })),
      })),
    }));
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    clearTimeout(dragTimerRef.current);
    if (!sceneRef.current) return;

    let item;
    try {
      const raw = e.dataTransfer.getData('application/json');
      if (!raw) return;
      item = JSON.parse(raw);
    } catch (err) { return; }

    // In OBJECT mode a dropped product becomes a placed object, not a finish.
    if (objectMode) { addObject(objectType, item); return; }

    const target = surfaceAtPointer(e.clientX, e.clientY);
    setDropTarget(null);

    if (!target) {
      // Dropped on empty space. This used to fall through to the back wall,
      // which silently changed a surface the user never aimed at.
      setLiveLabel('Drop a finish onto a wall, the floor or the ceiling');
      setTimeout(() => setLiveLabel(null), 2200);
      return;
    }

    if (target.kind === 'object') { applyToObject(target.id, item); return; }
    if (target.kind === 'patch') { applyToPatch(target.id, item); return; }
    if (onDropTile) onDropTile(target.id, item);
  };

  /* ---- inspector for the current selection ---- */

  const selectedObject = selection?.kind === 'object'
    ? (schedule?.objects || []).find((o) => o.id === selection.id)
    : null;
  const selectedPatch = selection?.kind === 'patch'
    ? ((schedule?.surfaces || []).find((s) => s.id === selection.surfaceId)?.patches || []).find((p) => p.id === selection.id)
    : null;
  const selectedAnnotation = selection?.kind === 'annotation'
    ? (schedule?.annotations || []).find((a) => a.id === selection.id)
    : null;

  const patchAnnotation = (changes) => update((prev) => ({
    ...prev,
    annotations: (prev.annotations || []).map((a) => (a.id === selection.id ? { ...a, ...changes } : a)),
  }));

  /** Role key ('back'|'left'|…) for a wall surface id, needed for its face width. */
  const roleOfSurface = (surfaceId) => {
    const roles = getSurfaceRoles(schedule?.surfaces || []);
    return ['back', 'left', 'right', 'front'].find((k) => roles[k] && roles[k].id === surfaceId);
  };

  /**
   * Typed dimensions go through the same bounds as dragging: cap the size to the
   * surface, then re-clamp the position so the item stays fully inside it.
   */
  const fitObject = (change) => {
    const o = selectedObject;
    if (!o) return change;
    const { width_ft: W, depth_ft: D, height_ft: H } = roomCfg;
    const b = extentsFor(o.anchor, roleOfSurface(o.surface_id), roomCfg);
    const next = { ...o, ...change };
    const w = clamp(next.w ?? 1, MIN_ITEM_FT, b.maxW);
    const h = clamp(next.h ?? 1, MIN_ITEM_FT, b.maxH);
    const out = { w, h };
    if (o.anchor === 'wall') {
      const halfW = b.maxW / 2;
      out.x = clamp(next.x ?? 0, -halfW + w / 2, halfW - w / 2);
      out.y = clamp(next.y ?? h / 2, h / 2, H - h / 2);
    } else if (o.anchor === 'rug') {
      out.x = clamp(next.x ?? 0, -W / 2 + w / 2, W / 2 - w / 2);
      out.z = clamp(next.z ?? 0, -D / 2 + h / 2, D / 2 - h / 2);
    } else {
      out.x = clamp(next.x ?? 0, -W / 2 + w / 2, W / 2 - w / 2);
      out.z = clamp(next.z ?? 0, -D / 2 + 0.2, D / 2 - 0.2);
    }
    return out;
  };

  const fitPatch = (change) => {
    const p = selectedPatch;
    if (!p) return change;
    const { height_ft: H } = roomCfg;
    const b = extentsFor('wall', roleOfSurface(selection?.surfaceId), roomCfg);
    const next = { ...p, ...change };
    const w = clamp(next.w_ft ?? 4, MIN_ITEM_FT, b.maxW);
    const h = clamp(next.h_ft ?? 8, MIN_ITEM_FT, b.maxH);
    const halfW = b.maxW / 2;
    return {
      w_ft: w,
      h_ft: h,
      x_ft: clamp(next.x_ft ?? 0, -halfW + w / 2, halfW - w / 2),
      y_ft: clamp(next.y_ft ?? h / 2, h / 2, H - h / 2),
    };
  };

  const patchObject = (changes) => update((prev) => ({
    ...prev,
    objects: (prev.objects || []).map((o) => (o.id === selection.id ? { ...o, ...changes } : o)),
  }));

  const patchPatch = (changes) => update((prev) => ({
    ...prev,
    surfaces: (prev.surfaces || []).map((s) => (s.id !== selection.surfaceId ? s : {
      ...s, patches: (s.patches || []).map((p) => (p.id === selection.id ? { ...p, ...changes } : p)),
    })),
  }));

  const btn = 'px-2 py-1 rounded text-[9px] font-black border transition-colors';
  const btnOff = 'text-white/45 border-white/15 hover:text-white/80';

  return (
    <div
      data-testid="room-visualizer-3d"
      style={{
        width: '100%', height: `${height}px`, borderRadius: 12, overflow: 'hidden',
        background: '#050505', position: 'relative',
        border: isDragOver ? '3px solid #22c55e' : '2px solid #1a1a1a',
        transition: 'border-color 0.15s',
      }}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      {/* ---- top toolbar ---- */}
      <div
        className="absolute top-0 left-0 right-0 z-10 flex flex-wrap items-center gap-2 px-3 py-2"
        style={{ background: 'linear-gradient(180deg, rgba(0,0,0,0.82) 0%, rgba(0,0,0,0) 100%)' }}
      >
        <DimInput label="W" value={roomCfg.width_ft} onCommit={(v) => setRoom({ width_ft: clamp(v, 2, 80) })} testId="room-width" />
        <DimInput label="D" value={roomCfg.depth_ft} onCommit={(v) => setRoom({ depth_ft: clamp(v, 2, 80) })} testId="room-depth" />
        <DimInput label="H" value={roomCfg.height_ft} onCommit={(v) => setRoom({ height_ft: clamp(v, 5, 30) })} testId="room-height" />

        <span className="text-white/15">|</span>

        <button
          className={`${btn} ${showLabels ? 'bg-amber-600 text-white border-amber-500' : btnOff}`}
          onClick={() => setShowLabels((v) => !v)}
          data-testid="toggle-labels"
          title="Show the name of each wall, floor and ceiling"
        >
          LABELS
        </button>

        <button
          className={`${btn} ${roomCfg.preset === 'shower' ? 'bg-amber-600 text-white border-amber-500' : btnOff}`}
          onClick={() => setRoom({ preset: roomCfg.preset === 'shower' ? 'room' : 'shower' })}
          data-testid="toggle-preset"
        >
          {roomCfg.preset === 'shower' ? 'SHOWER' : 'ROOM'}
        </button>
        <button
          className={`${btn} ${lighting === 'soft' ? 'bg-sky-600 text-white border-sky-500' : btnOff}`}
          onClick={() => setRoom({ lighting: lighting === 'soft' ? 'flat' : 'soft' })}
          data-testid="toggle-lighting"
        >
          {lighting === 'soft' ? 'LIT' : 'TRUE COLOR'}
        </button>
        <button
          className={`${btn} ${roomCfg.wall_count >= 4 ? 'bg-white/20 text-white border-white/30' : btnOff}`}
          onClick={() => setRoom({ wall_count: roomCfg.wall_count >= 4 ? 3 : 4 })}
          data-testid="toggle-walls"
        >
          {roomCfg.wall_count >= 4 ? '4 WALLS' : '3 WALLS'}
        </button>

        <span className="text-white/15">|</span>

        <button
          className={`${btn} ${objectMode ? 'bg-green-600 text-white border-green-500' : btnOff}`}
          onClick={() => setObjectMode(!objectMode)}
          data-testid="toggle-object-mode"
        >
          {objectMode ? 'DROP = OBJECT' : 'DROP = FINISH'}
        </button>
        <select
          value={objectType}
          onChange={(e) => setObjectType(e.target.value)}
          className="px-1.5 py-1 rounded bg-black/60 border border-white/15 text-white text-[10px] font-bold"
          data-testid="object-type-select"
        >
          {OBJECT_PRESETS.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
        </select>
        <button className={`${btn} ${btnOff}`} onClick={() => addObject(objectType, selectedItem)} data-testid="add-object-btn">+ ADD</button>
        <button className={`${btn} ${btnOff}`} onClick={() => addPatch(selectedItem)} data-testid="add-patch-btn">+ PANEL</button>

        <span className="text-white/15">|</span>

        <button
          className={`${btn} ${measureMode ? 'bg-emerald-600 text-white border-emerald-500' : btnOff}`}
          onClick={() => {
            const next = !measureMode;
            setMeasureMode(next);
            setMeasureTarget('auto');
            if (!next) setPlanView(false);
            pendingRef.current = null;
            setLiveLabel(next ? 'Click two points on the surface you want' : null);
          }}
          data-testid="toggle-measure"
        >
          {measureMode ? 'MEASURING' : '↔ MEASURE'}
        </button>

        {/* Which surface the two clicks land on. The floor is only a sliver of
            the view in a tall room, so leaving it to "whatever is nearest"
            makes floor and ceiling dimensions impractical to place. */}
        {measureMode && (
          <>
            <span className="text-[9px] font-bold text-white/40">ON:</span>
            <button
              className={`${btn} ${planView ? 'bg-sky-700 text-white border-sky-500' : btnOff}`}
              data-testid="toggle-plan-view"
              title="Look straight down — the whole floor in view"
              onClick={() => setPlanView((v) => !v)}
            >
              {planView ? 'PLAN VIEW' : '⌖ PLAN'}
            </button>
            {[['auto', 'WALLS'], ['floor', 'FLOOR'], ['ceiling', 'CEILING']].map(([key, label]) => (
              <button
                key={key}
                className={`${btn} ${measureTarget === key ? 'bg-emerald-700 text-white border-emerald-500' : btnOff}`}
                data-testid={`measure-target-${key}`}
                onClick={() => {
                  setMeasureTarget(key);
                  pendingRef.current = null;
                  setLiveLabel(key === 'auto'
                    ? 'Click two points on a wall'
                    : `Click two points on the ${key}`);
                }}
              >
                {label}
              </button>
            ))}
          </>
        )}
      </div>

      {/* ---- live size readout while dragging ---- */}
      {liveLabel && (
        <div
          className="absolute z-20 px-3 py-1 rounded font-black text-[12px]"
          style={{ left: '50%', top: 52, transform: 'translateX(-50%)', background: 'rgba(0,0,0,0.85)', color: '#22c55e', border: '1px solid rgba(34,197,94,0.45)' }}
          data-testid="live-dimension"
        >
          {liveLabel}
        </div>
      )}

      {/* ---- inspector ---- */}
      {(selectedObject || selectedPatch || selectedAnnotation) && (
        <div
          className="absolute z-20 bottom-3 left-3 rounded-lg p-3 space-y-2"
          style={{ background: 'rgba(10,10,14,0.94)', border: '1px solid rgba(255,255,255,0.14)', minWidth: 236 }}
          data-testid="viz-inspector"
        >
          <div className="flex items-center justify-between gap-3">
            <span className="text-[10px] font-black text-white truncate max-w-[150px]">
              {selectedObject ? (selectedObject.name || selectedObject.type)
                : selectedAnnotation ? 'Dimension'
                  : (selectedPatch.material?.name || 'Material panel')}
            </span>
            <button onClick={deleteSelection} className="text-[9px] font-black text-red-400 border border-red-400/30 rounded px-2 py-0.5" data-testid="viz-delete">
              DELETE
            </button>
          </div>

          <div className="flex flex-wrap gap-2">
            {selectedObject ? (
              <>
                <DimInput label="W" value={selectedObject.w} onCommit={(v) => patchObject(fitObject({ w: v }))} testId="sel-w" />
                <DimInput label="H" value={selectedObject.h} onCommit={(v) => patchObject(fitObject({ h: v }))} testId="sel-h" />
                {selectedObject.anchor === 'wall' && (
                  <DimInput label="A.F.F." value={selectedObject.y} onCommit={(v) => patchObject(fitObject({ y: v }))} testId="sel-y" />
                )}
              </>
            ) : selectedAnnotation ? (
              <div className="space-y-2 w-full">
                <div className="text-[10px] text-white/60">
                  Measured{' '}
                  <span className="text-white font-bold" data-testid="annot-measured">
                    {formatFeet(Math.hypot(
                      selectedAnnotation.bx - selectedAnnotation.ax,
                      selectedAnnotation.by - selectedAnnotation.ay,
                    ))}
                  </span>{' '}
                  — drag either end to adjust
                </div>
                <label className="flex items-center gap-1">
                  <span className="text-[9px] font-bold text-white/40">LABEL</span>
                  <input
                    value={selectedAnnotation.label || ''}
                    onChange={(e) => patchAnnotation({ label: e.target.value })}
                    placeholder="auto (measured)"
                    className="flex-1 px-1.5 py-1 rounded bg-black/60 border border-white/15 text-white text-[10px] font-bold"
                    data-testid="annot-label-input"
                  />
                </label>
                <div className="flex gap-1">
                  {ANNOTATION_COLORS.map((c) => (
                    <button
                      key={c}
                      onClick={() => patchAnnotation({ color: c })}
                      style={{ background: c, width: 18, height: 18, borderRadius: 3, border: '1px solid rgba(255,255,255,0.25)' }}
                      data-testid={`annot-color-${c.replace('#', '')}`}
                    />
                  ))}
                </div>
              </div>
            ) : (
              <>
                <DimInput label="W" value={selectedPatch.w_ft} onCommit={(v) => patchPatch(fitPatch({ w_ft: v }))} testId="sel-pw" />
                <DimInput label="H" value={selectedPatch.h_ft} onCommit={(v) => patchPatch(fitPatch({ h_ft: v }))} testId="sel-ph" />
                <DimInput label="A.F.F." value={selectedPatch.y_ft} onCommit={(v) => patchPatch(fitPatch({ y_ft: v }))} testId="sel-py" />
              </>
            )}
          </div>

          {selectedObject && selectedObject.anchor !== 'wall' && (
            <div className="flex items-center gap-2">
              <span className="text-[9px] font-bold text-white/40">ROTATE</span>
              <input
                type="range" min={0} max={360} step={5}
                value={Math.round(((selectedObject.rot_y || 0) * 180) / Math.PI)}
                onChange={(e) => patchObject({ rot_y: (Number(e.target.value) * Math.PI) / 180 })}
                className="flex-1"
                data-testid="sel-rotate"
              />
            </div>
          )}

          {selectedPatch && (
            <div className="flex flex-wrap gap-1">
              {PAINT_SWATCHES.map((s) => (
                <button
                  key={s.hex}
                  title={s.name}
                  onClick={() => patchPatch({ material: { ...(selectedPatch.material || {}), swatch_color: s.hex, image: '', name: s.name } })}
                  style={{ background: s.hex, width: 18, height: 18, borderRadius: 3, border: '1px solid rgba(255,255,255,0.25)' }}
                  data-testid={`patch-swatch-${s.hex}`}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {isDragOver && (
        <div
          data-testid="drop-overlay"
          className="absolute inset-0 flex items-center justify-center z-10"
          style={{ background: 'rgba(34,197,94,0.06)', pointerEvents: 'none', borderRadius: 12 }}
        >
          <span style={{
            color: '#22c55e', fontSize: 20, fontWeight: 900, letterSpacing: 3,
            textShadow: '0 2px 12px rgba(0,0,0,0.9)', padding: '10px 24px', borderRadius: 8,
            background: 'rgba(0,0,0,0.7)', border: '2px solid #22c55e',
          }}>
            {objectMode ? 'DROP TO PLACE OBJECT' : 'DROP FINISH HERE'}
          </span>
        </div>
      )}

      <Canvas
        // Explicit size: r3f otherwise leaves the canvas at the 300x150 HTML
        // default when its first measurement lands before layout settles.
        style={{ width: '100%', height: '100%', display: 'block' }}
        resize={{ scroll: false, debounce: { scroll: 0, resize: 0 } }}
        camera={{ position: [0, roomCfg.height_ft * 0.48, roomCfg.depth_ft * 1.15], fov: 52 }}
        frameloop="demand"
        gl={{ antialias: true, toneMapping: THREE.NoToneMapping, outputColorSpace: THREE.SRGBColorSpace }}
        onCreated={({ gl }) => { gl.setClearColor('#050505'); }}
        onPointerMissed={() => setSelection(null)}
      >
        {/* Provider lives inside the Canvas: r3f runs its own reconciler, so
            context from the DOM tree is not guaranteed to cross the boundary. */}
        <VizContext.Provider value={ctx}>
          <Scene schedule={schedule} sceneRef={sceneRef} dragging={dragging} />
        </VizContext.Provider>
      </Canvas>
    </div>
  );
};

export default RoomVisualizer3D;
